import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import InputBase from '@mui/material/InputBase';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useCompanyStore } from '../../stores/companyStore';
import { flattenItems, type NavItem } from './navCatalog';
import {
  summaryAllStatuses,
  REVIEW_STATUS_LABELS_HE,
  REVIEW_STATUS_COLORS,
} from '../../api/companyStatus';

interface PaletteItem {
  kind: 'page' | 'company' | 'action';
  id: string;
  label: string;
  /** lowercased haystack the fuzzy matcher checks against */
  haystack: string;
  /** for pages: route. for companies: company id. for actions: a fn key */
  payload: string;
  /** Hebrew label of the source ("דף", "חברה", "פעולה") */
  sourceLabel: string;
  /** optional status pill on company rows */
  statusKey?: keyof typeof REVIEW_STATUS_LABELS_HE | null;
  /** optional secondary hint shown to the right of the label */
  hint?: string | null;
  /** for pages: the icon */
  icon?: React.ReactNode;
  /** for actions: the function to invoke */
  run?: () => void;
}

/** Crude subsequence fuzzy match — each query char must appear in
 * order in the haystack. Returns a quality score: lower = better.
 * Negative return = no match. */
function fuzzyScore(query: string, haystack: string): number {
  if (!query) return 0;
  let qi = 0;
  let score = 0;
  let lastMatchIdx = -1;
  for (let i = 0; i < haystack.length && qi < query.length; i++) {
    if (haystack[i] === query[qi]) {
      // Penalize gaps between matched characters; bonus for word boundaries.
      score += (lastMatchIdx >= 0 ? (i - lastMatchIdx) : i);
      lastMatchIdx = i;
      qi++;
    }
  }
  return qi === query.length ? score : -1;
}

// Plain-Hebrew glossary of the cryptic Hashavshevet tables/fields the
// wiz-* pages surface — searchable from the palette.
const GLOSSARY: { term: string; he: string }[] = [
  { term: 'JurnalTrans', he: 'כותרת תנועת יומן — תאריך, צדדים, סכום, אסמכתא' },
  { term: 'JurnalTransMoves', he: 'שורות תנועת היומן (DebitCredit: 1=חובה, 0=זכות)' },
  { term: 'Accounts', he: 'כרטסת חשבונות / לקוחות / ספקים (138 עמודות)' },
  { term: 'MainAccount', he: 'קבוצה ראשית: 1=לקוחות 2=ספקים 3=הוצאות 4=בנקים 12=מע״מ עסקאות 13=תשומות 23=מקדמות' },
  { term: 'Maam874Moves', he: 'שורות PCN874: Suf=מע״מ, MoreSuf2=בסיס, AccFileNum=ח.פ' },
  { term: 'Maam874Issues', he: 'דיווחי מע״מ שהוגשו (סיכום חודשי)' },
  { term: 'BankPages', he: 'דפי בנק מיובאים — Recon=1 הותאם, Ballance=יתרה' },
  { term: 'TaxApiNumber', he: 'מספר הקצאה — מודל "חשבונית ישראל" של רשות המסים' },
  { term: 'TaxRepCode', he: 'קוד סיווג לדוחות כספיים (עץ TaxReport)' },
  { term: 'DeductionPrc', he: 'אחוז ניכוי מס במקור על הספק' },
  { term: 'AvgLatePay', he: 'ממוצע ימי פיגור בתשלום של הלקוח' },
  { term: 'suF / suFDlr', he: 'סכום בש״ח / סכום במט״ח' },
  { term: 'ValueDate / BDate', he: 'תאריך ערך / תאריך הרישום בפועל (פער = רישום באיחור)' },
  { term: 'FullMatch', he: 'האם פריט פתוח נסגר במלואו (0=פתוח)' },
  { term: 'Dumi', he: 'חשבון/פריט דמה או מערכת (1) — מסונן מהתצוגות' },
  { term: 'Hour / PTime', he: 'שדות שעה = דקות מחצות (944 = 15:44)' },
];

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export default function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companies, selectCompany, recentIds } = useCompanyStore();

  const statusQ = useQuery({
    queryKey: ['company-status', 'summary-all'],
    queryFn: () => summaryAllStatuses(),
    staleTime: 30_000,
    enabled: open,
  });
  const statusById = useMemo(() => {
    const m = new Map<string, ReturnType<typeof statusQ.data extends infer T ? (T extends Array<infer U> ? U : never) : never>>();
    for (const s of statusQ.data ?? []) m.set(s.company_id, s);
    return m;
  }, [statusQ.data]);

  const recentSet = useMemo(() => new Set(recentIds), [recentIds]);

  // Build the merged item list once per (companies, t) change. The
  // fuzzy matcher then filters/scores against the query.
  const allItems: PaletteItem[] = useMemo(() => {
    const pages: PaletteItem[] = flattenItems(true).map((it: NavItem) => {
      const labelText = it.label.startsWith('nav.') ? (t(it.label) || it.label) : it.label;
      return {
        kind: 'page',
        id: it.to,
        label: labelText,
        haystack: `${labelText} ${it.to}`.toLowerCase(),
        payload: it.to,
        sourceLabel: 'דף',
        icon: it.icon,
      };
    });
    const companyItems: PaletteItem[] = companies.map((c) => {
      const status = statusById.get(c.id);
      return {
        kind: 'company',
        id: c.id,
        label: c.name,
        haystack: `${c.name} ${c.code ?? ''}`.toLowerCase(),
        payload: c.id,
        sourceLabel: 'חברה',
        statusKey: (status?.status as PaletteItem['statusKey']) ?? null,
        hint: c.code || null,
      };
    });
    // Verbs — quick one-shot actions the palette can run directly.
    const actions: PaletteItem[] = [
      {
        kind: 'action',
        id: 'print',
        label: 'הדפס דף נוכחי',
        haystack: 'הדפס הדפסה print pdf',
        payload: 'print',
        sourceLabel: 'פעולה',
        run: () => window.print(),
      },
      {
        kind: 'action',
        id: 'logout',
        label: 'התנתק',
        haystack: 'התנתק logout signout',
        payload: 'logout',
        sourceLabel: 'פעולה',
        run: () => {
          try {
            localStorage.removeItem('hash_token');
            localStorage.removeItem('hash_refresh_token');
          } catch { /* ignore */ }
          window.location.href = '/login';
        },
      },
      {
        kind: 'action',
        id: 'reload',
        label: 'רענן נתונים (Hard reload)',
        haystack: 'רענן רענון reload refresh',
        payload: 'reload',
        sourceLabel: 'פעולה',
        run: () => window.location.reload(),
      },
    ];
    const glossary: PaletteItem[] = GLOSSARY.map((g) => ({
      kind: 'action',
      id: `gloss:${g.term}`,
      label: g.term,
      haystack: `${g.term} ${g.he}`.toLowerCase(),
      payload: 'glossary',
      sourceLabel: 'מילון',
      hint: g.he,
      run: () => { /* informational only */ },
    }));
    return [...pages, ...companyItems, ...actions, ...glossary];
  }, [companies, t, statusById]);

  const [query, setQuery] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIdx(0);
      // Defer focus so the dialog has rendered.
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      // Empty query — show pages first (alpha), then recent companies
      // (in MRU order) so the palette is useful even before typing.
      const pages = allItems.filter((i) => i.kind === 'page');
      const recentCompanies = recentIds
        .map((id) => allItems.find((i) => i.kind === 'company' && i.id === id))
        .filter((i): i is PaletteItem => !!i);
      return [...pages, ...recentCompanies].slice(0, 40);
    }
    const scored = allItems
      .map((it) => ({ it, score: fuzzyScore(q, it.haystack) }))
      .filter((s) => s.score >= 0);
    scored.sort((a, b) => {
      // Recent companies break ties in their favor.
      const aRecent = a.it.kind === 'company' && recentSet.has(a.it.id) ? 0 : 1;
      const bRecent = b.it.kind === 'company' && recentSet.has(b.it.id) ? 0 : 1;
      if (a.score !== b.score) return a.score - b.score;
      return aRecent - bRecent;
    });
    return scored.slice(0, 40).map((s) => s.it);
  }, [allItems, query, recentIds, recentSet]);

  useEffect(() => {
    // Keep selection inside the visible range when filter changes.
    if (selectedIdx >= filtered.length) setSelectedIdx(0);
  }, [filtered.length, selectedIdx]);

  const activate = (item: PaletteItem) => {
    if (item.kind === 'page') {
      navigate(item.payload);
    } else if (item.kind === 'company') {
      selectCompany(item.payload);
    } else if (item.kind === 'action' && item.run) {
      item.run();
    }
    onClose();
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const it = filtered[selectedIdx];
      if (it) activate(it);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          // Lift the modal closer to the top of the viewport (palette
          // convention) so it doesn't cover content the user is
          // searching for context about.
          position: 'absolute',
          top: '12%',
          m: 0,
          borderRadius: 2,
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1.25,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <SearchIcon sx={{ color: 'text.secondary' }} />
          <InputBase
            inputRef={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIdx(0);
            }}
            onKeyDown={onKey}
            placeholder="חפש דף או חברה… (Esc לסגירה, ↑↓ ניווט, Enter בחירה)"
            sx={{ flexGrow: 1, fontSize: 15 }}
          />
        </Box>
        <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {filtered.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary', fontSize: 13 }}>
              לא נמצא דבר
            </Box>
          ) : (
            filtered.map((it, idx) => {
              const isSelected = idx === selectedIdx;
              const pill = it.statusKey ? REVIEW_STATUS_COLORS[it.statusKey] : null;
              return (
                <Box
                  key={`${it.kind}:${it.id}`}
                  onMouseEnter={() => setSelectedIdx(idx)}
                  onClick={() => activate(it)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    px: 2,
                    py: 1.1,
                    cursor: 'pointer',
                    bgcolor: isSelected ? '#EFF6FF' : 'transparent',
                    borderInlineStart: isSelected ? '3px solid #2563EB' : '3px solid transparent',
                  }}
                >
                  <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                    {it.kind === 'page' ? it.icon : <BusinessIcon fontSize="small" />}
                  </Box>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: isSelected ? 600 : 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {it.label}
                    </Typography>
                    {it.hint && (
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: 'text.disabled',
                          fontFamily: 'monospace',
                        }}
                      >
                        {it.hint}
                      </Typography>
                    )}
                  </Box>
                  {pill && it.statusKey && (
                    <Box
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        px: 0.75,
                        py: 0.125,
                        borderRadius: 1,
                        bgcolor: pill.bg,
                        color: pill.fg,
                      }}
                    >
                      {REVIEW_STATUS_LABELS_HE[it.statusKey]}
                    </Box>
                  )}
                  <Typography
                    sx={{ fontSize: 10, color: 'text.disabled', textTransform: 'uppercase' as const }}
                  >
                    {it.sourceLabel}
                  </Typography>
                  <ChevronRightIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                </Box>
              );
            })
          )}
        </Box>
        <Divider />
        <Box
          sx={{
            px: 2,
            py: 0.75,
            display: 'flex',
            justifyContent: 'space-between',
            color: 'text.disabled',
            fontSize: 11,
          }}
        >
          <Box>{filtered.length} תוצאות</Box>
          <Box>⌘K · Ctrl+K לפתיחה</Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
